import { useCallback, useMemo } from "react";
import { useUnmount } from "react-use";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { differenceWith, isEqual } from "lodash";

import { Para16Text, Para14Text } from "components/shared/Typography";
import { SALES_EMAIL } from "helpers/constants";
import usePreference from "hooks/usePreference";
import { PlatformType } from "types/design";
import { IKitStatus } from "types/preference";

import { PlatformSetting } from "./PlatformSetting";

export const SequencingEstimatorPreference = () => {
  const {
    currentKits,
    loading,
    preferences,
    setCurrentKits,
    setPreferences,
    updatePreferences,
  } = usePreference();
  const inNarrowMode: boolean = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const changedKits: IKitStatus[] = useMemo(() => {
    if (!preferences?.sequencingEstimator) return [];

    return differenceWith(
      currentKits,
      preferences.sequencingEstimator,
      isEqual
    );
  }, [preferences?.sequencingEstimator, currentKits]);

  useUnmount(() => {
    setCurrentKits(preferences?.sequencingEstimator || []);
  });

  const resetChanges = useCallback(() => {
    setCurrentKits(preferences?.sequencingEstimator || []);
  }, [setCurrentKits, preferences?.sequencingEstimator]);

  const saveChanges = useCallback(() => {
    updatePreferences(
      {
        sequencingEstimator: changedKits.map((kit) => ({
          kit: kit.kit,
          isActive: kit.isActive,
          status: "active",
        })),
      },
      () => {
        setPreferences((preferences) => ({
          ...preferences,
          sequencingEstimator: currentKits,
        }));
      }
    );
  }, [setPreferences, currentKits, changedKits, updatePreferences]);

  return (
    <Accordion square expanded>
      <AccordionSummary>
        <Typography variant="h2">Sequencing Estimator</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" flexDirection="column" gap="4px">
            <Para16Text>
              Choose which kits are displayed by default in the Sequencing
              Estimator for all panels within Assay Marketplace.
            </Para16Text>
            <Para14Text>
              Looking for a kit that’s not listed?{" "}
              <Link
                color="secondary"
                href={`mailto:${SALES_EMAIL}`}
                target="_blank"
                sx={{ textDecorationColor: "unset" }}
              >
                Email customer service.
              </Link>
            </Para14Text>
          </Box>

          <Box display="flex" flexDirection="column" gap="24px">
            <PlatformSetting platformType={PlatformType.ILLUMINA} />

            <PlatformSetting platformType={PlatformType.IONTORRENT} />
          </Box>

          <Box
            display="flex"
            flexDirection={inNarrowMode ? "column-reverse" : "row"}
            justifyContent="flex-end"
            gap="16px"
            marginY={inNarrowMode ? 2 : 0}
          >
            <Button
              variant="text"
              disabled={!changedKits.length || loading}
              onClick={resetChanges}
              fullWidth={inNarrowMode}
            >
              Cancel
            </Button>
            <Button
              disabled={!changedKits.length || loading}
              onClick={saveChanges}
              fullWidth={inNarrowMode}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
